/**
 * Common Types - Shared between Frontend and Backend
 * Basic utility types and common interfaces only
 */

// Re-export pagination types from platform (canonical location)
export type { PaginationParams, PaginatedResponse } from '../platform/api/pagination.js'

// Sorting types
export type SortDirection = 'asc' | 'desc'

export interface SortConfig<T = string> {
  field: T
  direction: SortDirection
}

// Selection state for bulk operations
export interface SelectionState {
  selectedIds: Set<string>
  isAllSelected: boolean
}

// Theme configuration
export interface ThemeConfig {
  mode: 'light' | 'dark'
  primaryColor: string
  accentColor: string
}

// Generic filter type
export interface BaseFilter {
  search?: string
  startDate?: Date
  endDate?: Date
}

// Client information for security and auditing
export interface ClientInfo {
  ipAddress: string
  userAgent: string
}

// Request context for API handlers
export interface RequestContext {
  userId?: string
  tenantId: string // Required for multi-tenant isolation
  userAgent?: string
  ipAddress?: string
  user?: Record<string, unknown> // For test compatibility and context access
  requestId?: string // For request tracking and debugging
}

// Locale configuration for i18n
export const SUPPORTED_LOCALES = ['en', 'es', 'de', 'fr', 'ja', 'ko', 'ar'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * Locales that ship an actual TRANSLATION CATALOGUE — every namespace file
 * translated under `backend/src/platform/i18n/locales/<lng>/`.
 *
 * ⚠️⚠️ NOT the same list as `SUPPORTED_LOCALES`. That one says which locales the
 * i18n layer ACCEPTS (`?locale=`, Accept-Language); `de`, `fr`, `ja`, `ko` and
 * `ar` are accepted but ship only an empty `common.json`. Measured 2026-09-23:
 * activating «Deutsch» in /admin/system/languages was allowed with no warning
 * (the guard read `SUPPORTED_LOCALES`), and a customer who picked it saw every
 * screen in English under a label reading «Deutsch». Whether a language can be
 * OFFERED to customers is this list's question.
 *
 * The backend guards it against the files on disk
 * (`translated-locales-match-the-catalogues.unit.test.ts`).
 */
export const TRANSLATED_LOCALES = ['en', 'es'] as const satisfies readonly SupportedLocale[]
export const DEFAULT_LOCALE = 'en' as const
